import { FunctionComponent, useEffect, useState } from 'react';
import { Prompt, useHistory } from 'react-router';
import { Location } from 'history';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';

import {
  BodyText,
  Button,
  Div,
  Flex,
  GrowlNotification,
  Gutter,
  Headline,
  InlineNotification,
  Loader,
  ModalDialog,
  Tag,
  Title,
} from '@gaiads/telia-react-component-library';
import { Card, GenericTable } from '@cdaas/reusable-components-daas';
import { ConflictError, ImportUsersRequest } from '@cdaas/api-clients/dist/asset-user-openapi';

import { NotificationActions, User } from '../../../../types';
import { importUsers, sanitizeEmployeeId } from '../../../../utils';
import { ErrorTranslations, getUsersWithPossibleErrors } from './utils';
import { modalClose, modalShow } from '../../../../store/actions';
import { RootState } from '../../../../store/reducers';
import { IMPORT_USERS_FLOW_CONSTANTS } from '../../../../utils/constants';

import UserEditModal from './UserEditModal/UserEditModal';
import { ErrorTypes, getCardProps, getRowConfig, groupUsersByGroup, UsersByGroup, UserWithPossibleError } from '../utils';

type ReviewAndSaveProps = {
  usersToImport: User[];
  setUsersToImport: (users: User[]) => void;
};

const ReviewAndSave: FunctionComponent<ReviewAndSaveProps> = ({ usersToImport, setUsersToImport }) => {
  const { t } = useTranslation('editOrganization');
  const history = useHistory();

  const [importedUsers, setImportedUsers] = useState<User[]>([]);
  const [usersWithPossibleErrors, setUsersWithPossibleErrors] = useState<UserWithPossibleError[]>([]);
  const [errorCount, setErrorCount] = useState<number>(0);
  const [errorCountWithoutBackendErrors, setErrorCountWithoutBackendErrors] = useState<number>(0);
  const [grouppedUsers, setGrouppedUsers] = useState<UsersByGroup[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedUserToEdit, setSelectedUserToEdit] = useState<UserWithPossibleError | null>(null);

  const [confirmCancelModalOpen, setConfirmCancelModalOpen] = useState<boolean>(false);
  const [confirmedCancel, setConfirmedCancel] = useState<boolean>(false);
  const [lastLocation, setLastLocation] = useState<Location>();

  const handleConfirmCancel = (location: Location): boolean => {
    if (!confirmedCancel) {
      setLastLocation(location);
      setConfirmCancelModalOpen(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (confirmedCancel && lastLocation) {
      history.replace(lastLocation?.pathname);
    }
  }, [confirmedCancel, lastLocation, history]);

  const dispatch = useDispatch();
  const removeUser = (id: string): void => {
    setImportedUsers(prevUsers => prevUsers.filter(user => user.id !== id));
    dispatch(modalClose());
  };

  const userGroups = useSelector((state: RootState) => state.userGroups.userGroups);

  const openDeleteModal = (user: UserWithPossibleError): NotificationActions => {
    const textDetails = {
      email: user?.email || t('usersAdmin.importUsers.reviewAndSave.errors.emailMissing'),
      phoneNo: user?.phone_number || t('usersAdmin.importUsers.reviewAndSave.errors.phoneNumberMissing'),
      userGroup: user?.user_group?.name || t('usersAdmin.importUsers.reviewAndSave.errors.nonExistingUserGroup'),
    };
    return dispatch(
      modalShow(
        t('usersAdmin.importUsers.reviewAndSave.deleteModal.title'),
        t('usersAdmin.importUsers.reviewAndSave.deleteModal.removeButton'),
        t('usersAdmin.importUsers.reviewAndSave.deleteModal.cancelButton'),
        () => removeUser(user.id!),
        () => dispatch(modalClose()),
        <BodyText>{t('usersAdmin.importUsers.reviewAndSave.deleteModal.message', textDetails)}</BodyText>,
      ),
    );
  };

  const openEditUserModal = (user: UserWithPossibleError) => {
    setSelectedUserToEdit(user);
    setModalOpen(true);
  };

  const closeEditUserModal = () => {
    setSelectedUserToEdit(null);
    setModalOpen(false);
  };

  const [loading, setLoading] = useState<boolean>(false);
  const [errorNotificationMessage, setErrorNotificationMessage] = useState<string>('');

  const errorTranslations: ErrorTranslations = {
    phoneNumberMissing: t('usersAdmin.importUsers.reviewAndSave.errors.phoneNumberMissing'),
    emailMissing: t('usersAdmin.importUsers.reviewAndSave.errors.emailMissing'),
    phoneNumberInvalid: t('usersAdmin.importUsers.reviewAndSave.errors.phoneNumberInvalid'),
    emailInvalid: t('usersAdmin.importUsers.reviewAndSave.errors.emailInvalid'),
    phoneNumberDuplicate: t('usersAdmin.importUsers.reviewAndSave.errors.phoneNumberDuplicate'),
    emailDuplicate: t('usersAdmin.importUsers.reviewAndSave.errors.emailDuplicate'),
    nonExistingUserGroup: t('usersAdmin.importUsers.reviewAndSave.errors.nonExistingUserGroup'),
    preScheduledOnboardingAtInPast: t('usersAdmin.importUsers.reviewAndSave.errors.preScheduledOnboardingAtInPast'),
    preScheduledOnboardingAtInvalid: t('usersAdmin.importUsers.reviewAndSave.errors.preScheduledOnboardingAtInvalid'),
  };

  useEffect(() => {
    setImportedUsers(usersToImport);
  }, [usersToImport]);

  useEffect(() => {
    const usersToImportWithPossibleErrors = getUsersWithPossibleErrors(importedUsers, errorTranslations);
    setUsersWithPossibleErrors(usersToImportWithPossibleErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importedUsers]);

  useEffect(() => {
    setErrorCount(usersWithPossibleErrors.filter(user => user.error).length);
    setErrorCountWithoutBackendErrors(
      usersWithPossibleErrors.filter(user => {
        if (!user.error) return false;
        return (
          user.error.errorType !== ErrorTypes.EmailDuplicateBetweenExistingUser &&
          user.error.errorType !== ErrorTypes.PhoneNumberDuplicateBetweenExistingUser
        );
      }).length,
    );
  }, [usersWithPossibleErrors]);

  useEffect(() => {
    const userWithoutGroup: User[] = [];
    const usersWithGroup = groupUsersByGroup(usersWithPossibleErrors, userWithoutGroup);
    for (const group of usersWithGroup) {
      group.users = group.users.sort((a, b) => {
        if (!a.error && !!b.error) {
          return 1;
        } else if (!!a.error && !b.error) {
          return -1;
        } else {
          return 0;
        }
      });
    }
    setGrouppedUsers(usersWithGroup);
  }, [usersWithPossibleErrors]);

  const constructSummaryErrorMessage = (users: UserWithPossibleError[]): string => {
    const errors = users.filter(user => user.error).map(user => user.error);

    const getPart = (errorTypes: ErrorTypes[], translationKey: string) => {
      const count = errors.filter(error => error && errorTypes.includes(error?.errorType)).length;
      if (count === 0) return;
      return t(`usersAdmin.importUsers.reviewAndSave.summaryErrors.${translationKey}`, { count });
    };

    const missingPhoneNumbers = getPart([ErrorTypes.PhoneNumberMissing], 'phoneNumberMissing');
    const missingEmails = getPart([ErrorTypes.EmailMissing], 'emailMissing');
    const invalidPhoneNumbers = getPart([ErrorTypes.PhoneNumberInvalid], 'phoneNumberInvalid');
    const invalidEmails = getPart([ErrorTypes.EmailInvalid], 'emailInvalid');
    const duplicatePhoneNumbers = getPart(
      [ErrorTypes.PhoneNumberDuplicateBetweenToBeImportedUsers, ErrorTypes.PhoneNumberDuplicateBetweenExistingUser],
      'phoneNumberDuplicate',
    );
    const duplicateEmails = getPart(
      [ErrorTypes.EmailDuplicateBetweenToBeImportedUsers, ErrorTypes.EmailDuplicateBetweenExistingUser],
      'emailDuplicate',
    );
    const nonExistingUserGroups = getPart([ErrorTypes.NonExistingUserGroup], 'nonExistingUserGroup');
    const invalidPreScheduledOnboardingAts = getPart([ErrorTypes.PreScheduledOnboardingAtInvalid], 'preScheduledOnboardingAtInvalid');
    const preScheduledOnboardingAtsInPast = getPart([ErrorTypes.PreScheduledOnboardingAtInPast], 'preScheduledOnboardingAtInPast');

    const allParts = [
      missingPhoneNumbers,
      missingEmails,
      invalidPhoneNumbers,
      invalidEmails,
      duplicatePhoneNumbers,
      duplicateEmails,
      nonExistingUserGroups,
      invalidPreScheduledOnboardingAts,
      preScheduledOnboardingAtsInPast,
    ];

    return allParts.filter(Boolean).join(', ');
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrorNotificationMessage('');
    let backendConflictError, backendConflictErrorMessage;

    const users = usersWithPossibleErrors.map(userWithPossibleError => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { error, ...userWithoutErrors } = userWithPossibleError;
      return userWithoutErrors;
    });

    try {
      const requestData: ImportUsersRequest[] = grouppedUsers
        .filter(groupOfUsers => groupOfUsers.userGroupId)
        .map(groupOfUsers => {
          const importUsersRequest: ImportUsersRequest = {
            user_group_id: groupOfUsers.userGroupId || '',
            users: groupOfUsers.users.map(user => ({
              email: user.email,
              phone_number: user.phone_number || '',
              employee_id: sanitizeEmployeeId(user.employee_id),
              prescheduled_onboarding_at: user.prescheduled_onboarding_at ?? undefined,
            })),
          };
          return importUsersRequest;
        });
      await importUsers(requestData);
      setUsersToImport(users);
      // TODO - Navigate to component telling the saving succeeded
      history.push(`${IMPORT_USERS_FLOW_CONSTANTS.summary}`);
    } catch (e) {
      // If error response status 409, should be error because of conflict
      if (isAxiosError(e) && e.response?.status === 409) {
        backendConflictError = (e.response?.data as ConflictError)?.error;
        backendConflictErrorMessage = (e.response?.data as ConflictError)?.message;
        setErrorNotificationMessage(t('usersAdmin.importUsers.reviewAndSave.backendErrorNotification.conflict'));
      } else {
        setErrorNotificationMessage(t('usersAdmin.importUsers.reviewAndSave.backendErrorNotification.general'));
      }
      const usersToImportWithPossibleErrors = getUsersWithPossibleErrors(users, errorTranslations, backendConflictError, backendConflictErrorMessage);
      // setUsersWithPossibleErrors should flow via useEffects to error counts, grouppedUsers etc.
      setUsersWithPossibleErrors(usersToImportWithPossibleErrors);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Div alignTextToCenter margin={{ vertical: 'xxlg' }}>
        <Loader />
      </Div>
    );
  }

  const handleSaveUser = (data: {
    id: string;
    email: string;
    phoneNumber: string;
    userGroup: string;
    employeeId: string | null;
    preScheduledOnboardingAt: string | null;
  }): void => {
    const updateUser = (user: User): User => {
      if (user.id !== data.id) return user;

      return {
        ...user,
        email: data.email,
        phone_number: data.phoneNumber,
        user_group: userGroups.find(ug => ug.id === data.userGroup),
        employee_id: sanitizeEmployeeId(data.employeeId),
        prescheduled_onboarding_at: data.preScheduledOnboardingAt,
      };
    };

    setImportedUsers(prevUsers => prevUsers.map(updateUser));
    closeEditUserModal();
  };

  return (
    <Div>
      <UserEditModal
        user={selectedUserToEdit}
        saveUserCallback={data => handleSaveUser(data)}
        closeCallback={closeEditUserModal}
        modalOpen={modalOpen}
      />
      <Headline size="md">{t('usersAdmin.importUsers.reviewAndSave.title')}</Headline>
      <Gutter size={'layout-7'} />
      <Title size="sm">{t('usersAdmin.importUsers.reviewAndSave.importedCount', { count: usersWithPossibleErrors.length })}</Title>
      <Gutter size={'sm'} />
      <BodyText>{t('usersAdmin.importUsers.reviewAndSave.saveInfo')}</BodyText>
      <Gutter size={'md'} />

      {errorCount > 0 && (
        <>
          <InlineNotification
            title={t('usersAdmin.importUsers.reviewAndSave.errorNotificationTitle', { count: errorCount })}
            messageText={constructSummaryErrorMessage(usersWithPossibleErrors)}
            variant="error"
            forceMultiline={true}
          />
          <Gutter size={'sm'} />
        </>
      )}
      <Gutter size="sm" />
      {grouppedUsers.map(group => {
        const groupErrorCount = group.users.filter(user => !!user.error).length;
        const cardProps = getCardProps(group, t);
        const rowConfig = getRowConfig();
        if (!group.userGroupId) {
          cardProps.tag = { text: t('usersAdmin.importUsers.reviewAndSave.errors.nonExistingUserGroupTag', { count: groupErrorCount }) };
          rowConfig.push({
            key: 'error_field',
            formatValue: () => <Tag color="red600">{t('usersAdmin.importUsers.reviewAndSave.errors.noUserGroupSelected')}</Tag>,
          });
        } else if (groupErrorCount) {
          cardProps.tag = { text: t('usersAdmin.importUsers.reviewAndSave.groupErrorCount', { count: groupErrorCount }) };
        }
        return (
          <Card {...cardProps} key={group.userGroup}>
            <GenericTable
              loading="none"
              inputData={group.users}
              noDataText={t('usersAdmin.importUsers.reviewAndSave.noUsersText')}
              rowConfigs={rowConfig}
              buttonConfig={{ name: t('usersAdmin.importUsers.reviewAndSave.editButton'), buttonClickCallback: user => openEditUserModal(user) }}
              iconConfig={[
                { iconProps: { name: 'delete', size: 'md', clickableAppearance: true }, buttonClickCallback: user => openDeleteModal(user) },
              ]}
              isGenericTableInsideCard={true}
            />
          </Card>
        );
      })}
      {errorNotificationMessage && (
        <GrowlNotification variant="error" notification={errorNotificationMessage} closeOnClick={() => setErrorNotificationMessage('')} />
      )}
      <Flex>
        <Button onClick={() => handleSubmit()} disabled={errorCountWithoutBackendErrors > 0} variant={'primary'}>
          {t('usersAdmin.importUsers.reviewAndSave.saveChangesBtn')}
        </Button>
        <Gutter size={'sm'} />
        <Button onClick={() => history.push('/users')} variant={'secondary'}>
          {t('usersAdmin.importUsers.reviewAndSave.cancelBtn')}
        </Button>
      </Flex>
      <Gutter size={'sm'} />

      <Prompt when={!confirmedCancel} message={handleConfirmCancel} />
      <ModalDialog
        isOpen={confirmCancelModalOpen}
        title={t('usersAdmin.importUsers.reviewAndSave.cancelModal.title')}
        acceptButtonText={t('usersAdmin.importUsers.reviewAndSave.cancelModal.acceptButton')}
        closeButtonText={t('usersAdmin.importUsers.reviewAndSave.cancelModal.closeButton')}
        onAcceptClick={() => setConfirmedCancel(true)}
        onCloseClick={() => {
          setConfirmCancelModalOpen(false);
          setConfirmedCancel(false);
        }}
      >
        <BodyText>{t('usersAdmin.importUsers.reviewAndSave.cancelModal.cancelConfirmText')}</BodyText>
      </ModalDialog>
    </Div>
  );
};

export default ReviewAndSave;
